import clsx from 'clsx'

/**
 * The assistant's mark.
 *
 * An iridescent sphere: a blurred conic sweep through the brand gradient, turning slowly,
 * under a specular highlight. The construction is in `styles/surfaces.css` (`.orb`), which
 * keeps the three stacked layers out of every call site; this component exists only to fix
 * the size and to decide which of the two treatments applies.
 *
 * **It means one thing.** The orb marks the presence of the model itself — the byline above
 * an answer, the home hero, the sign-in hero. It is not decoration and it is not an avatar:
 * an *agent* wears its own accent tile (see `marketplace/accents.ts`), because an agent is a
 * configured thing a person made, and conflating the two would put the machine's face on
 * someone's listing.
 *
 * **Call `AssistantMark`, not this.** A deployment whose assistant has a face of its own
 * supplies one (`brand/profiles.ts`), and every placement of the model's presence has to
 * honour that or the product ends up half-branded — Gi's mark in the rail and a violet
 * sphere over its answers. This component is what `AssistantMark` falls back to, and it is
 * the right fallback for almost every deployment: the orb is painted from the brand seeds,
 * so it arrives in the customer's colour with nobody drawing anything.
 *
 * Always `aria-hidden`. Every placement sits beside text that already names what it marks.
 */
export const Orb = ({
  size = 22,
  hero = false,
  className
}: {
  size?: number
  /** The 98px treatment: heavier blur, slower turn, real inner shadow and a cast glow. */
  hero?: boolean
  className?: string
}) => <span aria-hidden style={{ width: size, height: size }} className={clsx('orb', hero && 'orb-hero', className)} />
